import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { merge } from "lodash";
import { Descriptions } from "../constants/descriptions";
import HelperText from "../../../components/atoms/HelperText";
import {
  ChromosomeCoordinatesAssemblyOptions,
  VariantTypeOptions,
  ChromosomeOptions,
} from "../constants/form-constants";
import { VariantSetType, ChromosomeCoordinatesType } from "../types/types";
import {
  ToggleButton,
  InformationMessageToast,
} from "../../../components/atoms";
import {
  Input,
  FormGroup,
  InputHelperText,
  Select,
  Label,
} from "../../../components/atoms/Form";
import { validationErrors } from "../constants/validationErrors";

const initialState = (chrCoords, hgvs) => {
  return {
    variantSet: {
      variant: [
        {
          chromosomeCoordinates: {
            // accession: "",
            alternateAllele: "",
            assembly: "",
            chromosome: "",
            referenceAllele: "",
            start: "",
            stop: "",
            // innerStart: 0,
            // innerStop: 0,
            // outerStart: 0,
            // outerStop: 0,
            // variantLength: 0,
          },
          // copyNumber: "",
          gene: [
            {
              id: "",
              symbol: "",
            },
          ],
          hgvs: hgvs || "",
          // referenceCopyNumber: 0,
          variantType: "",
        },
      ],
    },
  };
};

const VariantSet = (props) => {
  const {
    onVariantChange,
    errors,
    touched,
    handleBlur,
    setErrors,
    setTouched,
    hgvs,
    defaults,
    chrCoords,
    statusCode,
  } = props;

  // const [showCNV, setShowCNV] = useState(false);
  const [state, setState] = useState(initialState(chrCoords, hgvs));
  const [variantSetMode, setVariantSetMode] = useState(
    hgvs ? "hgvs" : "chromosome"
  );

  /* When switching between hgvs and chromosome coordinates radio buttons prepopulate the correct fields */
  const handleModeChange = (value) => {
    const newState = { ...state };
    const stateChrCoords = newState.variantSet.variant[0].chromosomeCoordinates;
    if (value === "hgvs") {
      const newErrors = { ...errors };
      newErrors.variant[0].chromosomeCoordinates.assembly = "";
      newErrors.variant[0].chromosomeCoordinates.chromosome = "";
      if (hgvs) {
        newErrors.variant[0].hgvs = "";
      } else {
        newErrors.variant[0].hgvs = validationErrors.emptyStringError;
      }
      setErrors(newErrors);

      Object.keys(stateChrCoords).forEach((index) => {
        stateChrCoords[index] = "";
      });
      if (hgvs) {
        newState.variantSet.variant[0].hgvs = hgvs;
      }
    }
    if (value === "chromosome") {
      const newErrors = { ...errors };
      newState.variantSet.variant[0].chromosomeCoordinates.referenceAllele =
        chrCoords?.referenceAllele || chrCoords?.ref || "";
      newState.variantSet.variant[0].chromosomeCoordinates.start =
        chrCoords?.start || "";
      newState.variantSet.variant[0].chromosomeCoordinates.stop =
        chrCoords?.stop || "";
      // newState.variantSet.variant[0].chromosomeCoordinates.accession = "";
      newState.variantSet.variant[0].chromosomeCoordinates.alternateAllele =
        chrCoords?.alternateAllele || chrCoords?.alt || "";
      newState.variantSet.variant[0].chromosomeCoordinates.assembly =
        chrCoords?.assembly || "";
      newState.variantSet.variant[0].chromosomeCoordinates.chromosome =
        chrCoords?.chromosome || chrCoords?.chr || "";
      newState.variantSet.variant[0].hgvs = "";
      if (newState.variantSet.variant[0].chromosomeCoordinates.assembly) {
        newErrors.variant[0].chromosomeCoordinates.assembly = "";
        newErrors.variant[0].chromosomeCoordinates.chromosome = "";
        newErrors.variant[0].hgvs = "";
        setErrors(newErrors);
      } else {
        newErrors.variant[0].chromosomeCoordinates.assembly =
          validationErrors.emptyStringError;
        newErrors.variant[0].hgvs = "";
        setErrors(newErrors);
      }
    }
    setState(newState);
  };

  useEffect(() => {
    setVariantSetMode(hgvs ? "hgvs" : "chromosome");
    handleModeChange(hgvs ? "hgvs" : "chromosome");
  }, [hgvs, chrCoords]);

  const onChangeGene = (e, index = null, geneindex = null) => {
    const { value, name } = e.target;
    const newstate = { ...state };
    if (name === "id") {
      if (value === "") {
        newstate.variantSet.variant[index].gene[geneindex][name] = value;
        setState(newstate);
        return;
      }
      // eslint-disable-next-line no-restricted-globals
      if (
        // eslint-disable-next-line no-restricted-globals
        isNaN(value) ||
        !Number.isInteger(Number(value)) ||
        value.includes(".")
      ) {
        newstate.variantSet.variant[index].gene[geneindex][name] = value;
        setState(newstate);
        return;
      }
      newstate.variantSet.variant[index].gene[geneindex][name] = parseInt(
        value,
        10
      );
      setState(newstate);
      return;
    }
    newstate.variantSet.variant[index].gene[geneindex][name] = value;
    setState(newstate);
  };

  const addGene = (index = null) => {
    const newstate = { ...state };
    newstate.variantSet.variant[index].gene.push({
      id: "",
      symbol: "",
    });
    setState(newstate);
    const newErrors = { ...errors };
    newErrors.variant[0].gene.push({
      id: ``,
    });
    setErrors(newErrors);

    const newTouched = { ...touched };
    newTouched.variant[0].gene.push({
      id: ``,
    });
    setTouched(newTouched);
  };

  const removeGene = (index = null, geneindex = null) => {
    const newstate = { ...state };
    newstate.variantSet.variant[index].gene.splice(geneindex, 1);
    setState(newstate);

    const newErrors = { ...errors };
    newErrors.variant[0].gene.splice(geneindex, 1);
    setErrors(newErrors);

    const newTouched = { ...touched };
    newTouched.variant[0].gene.splice(geneindex, 1);
    setTouched(newTouched);
  };

  /* Changes state when user types in HGVS input. */
  const onChangeVariant = (e, index) => {
    const { value, name } = e.target;
    const newstate = { ...state };
    newstate.variantSet.variant[index][name] = value;
    setState(newstate);
  };

  /* Changes state when user types in Chromosome coordinates inputs. */
  const onChangechromoCoordinates = (e, index) => {
    const { name, value } = e.target;
    const newstate = { ...state };
    // eslint-disable-next-line no-restricted-globals
    if (
      (name === "start" || name === "stop") &&
      // eslint-disable-next-line no-restricted-globals
      (isNaN(value) || !Number.isInteger(Number(value)) || value.includes("."))
    ) {
      newstate.variantSet.variant[index].chromosomeCoordinates[name] = value;
      setState(newstate);
      return;
    }
    // eslint-disable-next-line no-restricted-globals
    if ((name === "start" || name === "stop") && !isNaN(value)) {
      if (value === "") {
        newstate.variantSet.variant[index].chromosomeCoordinates[name] = value;
        setState(newstate);
        return;
      }
      newstate.variantSet.variant[index].chromosomeCoordinates[name] = parseInt(
        value,
        10
      );
      setState(newstate);
      return;
    }
    newstate.variantSet.variant[index].chromosomeCoordinates[name] = value;
    setState(newstate);
  };

  useEffect(() => {
    onVariantChange({ ...state });
  }, [state]);

  // const handleAddvariant = () => {
  //   const newstate = { ...state };
  //   newstate.variantSet.variant.push({
  //     chromosomeCoordinates: {
  //       accession: "",
  //       alternateAllele: "",
  //       assembly: "",
  //       chromosome: "",
  //       innerStart: "",
  //       innerStop: "",
  //       outerStart: "",
  //       outerStop: "",
  //       referenceAllele: "",
  //       start: "",
  //       stop: "",
  //       variantLength: "",
  //     },
  //     copyNumber: "",
  //     gene: [
  //       {
  //         id: "",
  //         symbol: "",
  //       },
  //     ],
  //     hgvs: "",
  //     referenceCopyNumber: "",
  //     variantType: "",
  //   });
  //   setState(newstate);
  // };

  // const handleRemoveVariant = (index) => {
  //   const newstate = { ...state };
  //   newstate.variantSet.variant.splice(index, 1);
  //   setState(newstate);
  // };
  useEffect(() => {
    const newState = { ...state };
    if (defaults) {
      if (defaults.variant[0]?.hgvs) {
        setVariantSetMode("hgvs");
      }
      if (defaults.variant[0]?.chromosomeCoordinates) {
        setVariantSetMode("chromosome");
      }
      merge(newState.variantSet, defaults);
      setState(newState);
    }

    if (variantSetMode === "chromosome") {
      handleModeChange("chromosome");
    } else {
      handleModeChange("hgvs");
    }

    if (!newState.variantSet.variant[0].variantType) {
      // if is deletion or deletion homopolymer
      const vType = chrCoords?.variantType;
      if (vType && vType.toLowerCase().includes("deletion")) {
        newState.variantSet.variant[0].variantType = "Deletion";
        setState(newState);
      }
    }
  }, []);

  return (
    <div className="tw-pt-8 tw-pb-8">
      <div className="tw-text-2xl">Variant Set</div>
      <div className="tw-text-sm">{Descriptions.variantSet}</div>

      <div className="tw-flex tw-flex-col tw-py-4">
        <div>
          <input
            type="radio"
            value="hgvs"
            name="variantSet"
            className="tw-cursor-pointer"
            checked={variantSetMode === "hgvs"}
            readOnly="readonly"
            onClick={() => {
              setVariantSetMode("hgvs");
              handleModeChange("hgvs");
            }}
          />
          <label
            className="tw-cursor-pointer tw-px-2"
            onClick={() => {
              setVariantSetMode("hgvs");
              handleModeChange("hgvs");
            }}
          >
            HGVS
          </label>
        </div>
        <div>
          <input
            type="radio"
            value="chromosome"
            name="variantSet"
            className="tw-cursor-pointer"
            checked={variantSetMode === "chromosome"}
            readOnly="readonly"
            onClick={() => {
              setVariantSetMode("chromosome");
              handleModeChange("chromosome");
            }}
          />
          <label
            className="tw-cursor-pointer tw-px-2"
            onClick={() => {
              setVariantSetMode("chromosome");
              handleModeChange("chromosome");
            }}
          >
            Chromosome Coordinates
          </label>
        </div>
      </div>
      <ToggleButton
        label="Enable CNV fields (CNVs are not currently supported)"
        isChecked={false}
        onToggleClick={() => {}}
        noPaddingAndMargin={true}
      />
      {state.variantSet != null && (
        <div className="tw-pt-6">
          {state.variantSet.variant.map((variant, index) => {
            const key = `${index}-${state.variantSet.variant}`;
            return (
              <div key={key}>
                {variantSetMode === "hgvs" && (
                  <div className="w-py-4">
                    <FormGroup className="tw-w-6/12">
                      <Label text="HGVS" />
                      <Input
                        type="text"
                        disabled={statusCode}
                        name="hgvs"
                        onBlur={handleBlur}
                        value={variant.hgvs}
                        onChange={(e) => onChangeVariant(e, index)}
                      />
                      {touched.variant[0].hgvs && errors.variant[0].hgvs ? (
                        <InputHelperText type="error">
                          {errors.variant[0].hgvs}
                        </InputHelperText>
                      ) : (
                        <InputHelperText>{Descriptions.hgvs}</InputHelperText>
                      )}
                    </FormGroup>
                    {/* reference copy number will be used for the CNVs */}
                    {/* <div className="form-group row">
                      <div className="col-6">
                        <label>Reference Copy Number</label>
                        <div className="input-group">
                          <input
                            type="text"
                            name="referenceCopyNumber"
                            id="referenceCopyNumber"
                            className="form-control"
                            value={variant.referenceCopyNumber}
                            onChange={(e) => onChangeVariant(e, index)}
                          />
                        </div>
                        <small className="text-muted form-text">
                          {Descriptions.referenceCopyNumber}
                        </small>
                      </div>
                    </div> */}
                  </div>
                )}
                {variantSetMode === "chromosome" && (
                  <React.Fragment>
                    <InformationMessageToast
                      className="tw-mb-2 tw-mt-0"
                      type="warning"
                      message="Important: When possible, the VarSome variant representation has been converted to match what ClinVar expects, which may be different than what you see in VarSome."
                    />
                    <FormGroup>
                      <FormGroup>
                        <div>
                          <span>
                            <strong>Chromosome Coordinates</strong>
                            <HelperText
                              customText={Descriptions.chromosomeCoordinates}
                            />
                          </span>
                        </div>
                      </FormGroup>
                      <FormGroup>
                        {/* <FormGroup className="tw-w-6/12">
                          <Label text="Accession" />
                          <Input
                            type="text"
                            name="accession"
                            id="accession"
                            disabled={
                              formStatus ||
                              variant.hgvs ||
                              variant.chromosomeCoordinates.assembly ||
                              variant.chromosomeCoordinates.chromosome
                            }
                            value={variant.chromosomeCoordinates.accession}
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                        </FormGroup> */}
                        <FormGroup className="tw-w-6/12">
                          <Label text="Assembly *" />
                          <Select
                            type="select"
                            name="assembly"
                            onBlur={handleBlur}
                            disabled={
                              statusCode ||
                              variant.hgvs ||
                              variant.chromosomeCoordinates.accession
                            }
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                            value={variant.chromosomeCoordinates.assembly}
                          >
                            <option value="" />
                            {ChromosomeCoordinatesAssemblyOptions.map(
                              (value) => {
                                return (
                                  <option key={value} value={value}>
                                    {value}
                                  </option>
                                );
                              }
                            )}
                          </Select>
                          {touched.variant[0].chromosomeCoordinates.assembly &&
                          errors.variant[0].chromosomeCoordinates.assembly ? (
                            <InputHelperText type="error">
                              {errors.variant[0].chromosomeCoordinates.assembly}
                            </InputHelperText>
                          ) : (
                            <InputHelperText>
                              {Descriptions.assembly}
                            </InputHelperText>
                          )}
                        </FormGroup>
                      </FormGroup>
                      <FormGroup vertical={true}>
                        <FormGroup className="tw-w-4/12 tw-mr-4">
                          <Label text="Chromosome" />
                          <Select
                            disabled={
                              statusCode ||
                              variant.hgvs ||
                              variant.chromosomeCoordinates.accession
                            }
                            name="chromosome"
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                            value={variant.chromosomeCoordinates.chromosome}
                            onBlur={handleBlur}
                          >
                            <option value="" />
                            {ChromosomeOptions.map((value) => {
                              return (
                                <option key={value} value={value}>
                                  {value}
                                </option>
                              );
                            })}
                          </Select>
                          {touched.variant[0].chromosomeCoordinates
                            .chromosome &&
                          errors.variant[0].chromosomeCoordinates.chromosome ? (
                            <InputHelperText type="error">
                              {
                                errors.variant[0].chromosomeCoordinates
                                  .chromosome
                              }
                            </InputHelperText>
                          ) : (
                            <InputHelperText>
                              {Descriptions.chromosome}
                            </InputHelperText>
                          )}
                        </FormGroup>
                        <FormGroup className="tw-w-4/12 tw-mr-4">
                          <Label text="Start" />
                          <Input
                            type="text"
                            name="start"
                            id="start"
                            disabled={statusCode}
                            onBlur={handleBlur}
                            value={variant.chromosomeCoordinates.start}
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                          {!touched.variant[0].chromosomeCoordinates.start ||
                          !errors.variant[0].chromosomeCoordinates.start ? (
                            <InputHelperText>
                              {Descriptions.start}
                            </InputHelperText>
                          ) : (
                            <InputHelperText type="error">
                              {errors.variant[0].chromosomeCoordinates.start}
                            </InputHelperText>
                          )}
                        </FormGroup>
                        <FormGroup className="tw-w-4/12">
                          <Label text="Stop" />
                          <Input
                            type="text"
                            name="stop"
                            disabled={statusCode}
                            onBlur={handleBlur}
                            id="stop"
                            value={variant.chromosomeCoordinates.stop}
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                          {!touched.variant[0].chromosomeCoordinates.stop ||
                          !errors.variant[0].chromosomeCoordinates.stop ? (
                            <InputHelperText>
                              {Descriptions.stop}
                            </InputHelperText>
                          ) : (
                            <InputHelperText type="error">
                              {errors.variant[0].chromosomeCoordinates.stop}
                            </InputHelperText>
                          )}
                        </FormGroup>
                      </FormGroup>
                      {/* <div className="form-group row">
                        <label className="col-sm-3 col-12 col-xs-12">
                          innerStart
                        </label>
                        <div className="col-sm-9 col-12 col-xs-12">
                          <div className="input-group">
                            <input
                              type="text"
                              name="innerStart"
                              id="innerStart"
                              placeholder="innerStart"
                              className="form-control"
                              value={variant.chromosomeCoordinates.innerStart}
                              onChange={(e) =>
                                onChangechromoCoordinates(e, index)
                              }
                            />
                          </div>
                          <small className="text-muted form-text">
                            {Descriptions.innerStart}
                          </small>
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 col-12 col-xs-12">
                          innerStop
                        </label>
                        <div className="col-sm-9 col-12 col-xs-12">
                          <div className="input-group">
                            <input
                              type="text"
                              name="innerStop"
                              id="innerStop"
                              placeholder="innerStop"
                              className="form-control"
                              value={variant.chromosomeCoordinates.innerStop}
                              onChange={(e) =>
                                onChangechromoCoordinates(e, index)
                              }
                            />
                          </div>
                          <small className="text-muted form-text">
                            {Descriptions.innerStop}
                          </small>
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 col-12 col-xs-12">
                          outerStart
                        </label>
                        <div className="col-sm-9 col-12 col-xs-12">
                          <div className="input-group">
                            <input
                              type="text"
                              name="outerStart"
                              id="outerStart"
                              placeholder="outerStart"
                              className="form-control"
                              value={variant.chromosomeCoordinates.outerStart}
                              onChange={(e) =>
                                onChangechromoCoordinates(e, index)
                              }
                            />
                          </div>
                          <small className="text-muted form-text">
                            {Descriptions.outerStart}
                          </small>
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 col-12 col-xs-12">
                          outerStop
                        </label>
                        <div className="col-sm-9 col-12 col-xs-12">
                          <div className="input-group">
                            <input
                              type="text"
                              name="outerStop"
                              id="outerStop"
                              placeholder="outerStop"
                              className="form-control"
                              value={variant.chromosomeCoordinates.outerStop}
                              onChange={(e) =>
                                onChangechromoCoordinates(e, index)
                              }
                            />
                          </div>
                          <small className="text-muted form-text">
                            {Descriptions.outerStop}
                          </small>
                        </div>
                      </div> */}
                      <FormGroup vertical={true}>
                        <FormGroup className="tw-w-6/12 tw-mr-4">
                          <Label text="Reference Allele" />
                          <Input
                            type="text"
                            name="referenceAllele"
                            id="referenceAllele"
                            disabled={statusCode}
                            value={
                              variant.chromosomeCoordinates.referenceAllele
                            }
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                          <InputHelperText>
                            {Descriptions.referenceAllele}
                          </InputHelperText>
                        </FormGroup>
                        <FormGroup className="tw-w-6/12">
                          <Label text="Alternate Allele" />
                          <Input
                            type="text"
                            name="alternateAllele"
                            id="alternateAllele"
                            disabled={statusCode}
                            value={
                              variant.chromosomeCoordinates.alternateAllele
                            }
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                          <InputHelperText>
                            {Descriptions.alternateAllele}
                          </InputHelperText>
                        </FormGroup>
                      </FormGroup>
                    </FormGroup>
                    {/* keep those fields for the CNV */}
                    {/* <div className="form-group row">
                      <div className="col-6">
                        <label>Variant Length</label>
                        <div className="input-group">
                          <input
                            type="text"
                            name="variantLength"
                            id="variantLength"
                            className="form-control"
                            value={variant.chromosomeCoordinates.variantLength}
                            onChange={(e) =>
                              onChangechromoCoordinates(e, index)
                            }
                          />
                        </div>
                        <small className="text-muted form-text">
                          {Descriptions.variantLength}
                        </small>
                      </div>
                      <div className="col-6">
                        <label>Copy Number</label>
                        <div className="input-group">
                          <input
                            type="text"
                            name="copyNumber"
                            id="copyNumber"
                            className="form-control"
                            value={variant.copyNumber}
                            onChange={(e) => onChangeVariant(e, index)}
                          />
                        </div>
                        <small className="text-muted form-text">
                          {Descriptions.copyNumber}
                        </small>
                      </div>
                    </div> */}
                  </React.Fragment>
                )}
                <FormGroup className="tw-w-6/12">
                  <Label text="Variant Type" />
                  <Select
                    type="select"
                    disabled={statusCode}
                    name="variantType"
                    onChange={(e) => onChangeVariant(e, index)}
                    value={variant.variantType}
                  >
                    <option value="" />
                    {VariantTypeOptions.map((value) => {
                      return (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      );
                    })}
                  </Select>
                  <InputHelperText>
                    {Descriptions.variantType}
                    <HelperText customText={Descriptions.variantTypeDetails} />
                  </InputHelperText>
                </FormGroup>
                <FormGroup className="tw-w-full">
                  <div className="col-6">
                    <span>
                      Gene
                      <HelperText customText={Descriptions.gene} />
                    </span>
                  </div>
                  {state.variantSet.variant[index].gene.length && (
                    <div className="tw-w-full tw-pt-4 tw-flex">
                      <div className="tw-flex tw-px-2 tw-w-11/12">
                        <div className="tw-flex tw-w-6/12">
                          <Label text="NCBI Gene Id" isRequired={true} />
                        </div>
                        <div className="tw-flex tw-w-6/12">
                          <Label
                            text="HGNC official Gene Symbol"
                            isRequired={true}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {state.variantSet.variant[index].gene.length &&
                    state.variantSet.variant[index].gene.map(
                      (gene, geneindex) => {
                        const gkey = `gene-${geneindex}`;
                        return (
                          <div
                            className="tw-flex tw-w-full tw-bg-gray-100 tw-pt-2.5"
                            key={gkey}
                          >
                            <div className="tw-flex tw-w-full tw-flex-wrap tw-px-2">
                              <div className="tw-flex tw-w-11/12">
                                <FormGroup className="tw-w-6/12 tw-mr-4">
                                  <Input
                                    type="text"
                                    name="id"
                                    id={`geneid-${gkey}`}
                                    value={gene.id}
                                    onBlur={(e) => handleBlur(e, geneindex)}
                                    disabled={statusCode || gene.symbol}
                                    onChange={(e) =>
                                      onChangeGene(e, index, geneindex)
                                    }
                                  />
                                  {touched.variant[0].gene[geneindex].id &&
                                  errors.variant[0].gene[geneindex].id ? (
                                    <InputHelperText type="error">
                                      {errors.variant[0].gene[geneindex].id}
                                    </InputHelperText>
                                  ) : (
                                    ""
                                  )}
                                </FormGroup>
                                <FormGroup className="tw-w-6/12">
                                  <Input
                                    type="text"
                                    name="symbol"
                                    value={gene.symbol}
                                    disabled={statusCode || gene.id}
                                    onChange={(e) =>
                                      onChangeGene(e, index, geneindex)
                                    }
                                  />
                                </FormGroup>
                              </div>

                              <div className="tw-flex tw-h-8 tw-align-middle tw-items-center tw-justify-center tw-w-1/12">
                                {state.variantSet.variant[index].gene.length >
                                  1 && (
                                  <div>
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      size="lg"
                                      fixedWidth={true}
                                      className="tw-px-1 tw-text-red-600 tw-cursor-pointer"
                                      onClick={() =>
                                        removeGene(index, geneindex)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    )}
                  {state.variantSet.variant[index].gene.length && (
                    <div className="tw-flex tw-px-2 tw-py-4 tw-flex-col tw-w-full">
                      <div className="tw-flex tw-m-4 tw-justify-center">
                        <button
                          type="button"
                          onClick={() => addGene(index)}
                          disabled={
                            state.variantSet.variant[index].gene[
                              state.variantSet.variant[index].gene.length - 1
                            ].id === "" &&
                            state.variantSet.variant[index].gene[
                              state.variantSet.variant[index].gene.length - 1
                            ].symbol === ""
                          }
                          className="btn btn-success"
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            fixedWidth={true}
                            className="tw-px-1 tw-text-white"
                          />
                          Add Gene
                        </button>
                      </div>
                    </div>
                  )}
                </FormGroup>
                {/* {state.variantSet.variant.length > 1 && (
                  <div className="col-12">
                    <FontAwesomeIcon
                      icon={faTrash}
                      size="lg"
                      fixedWidth={true}
                      onClick={() => handleRemoveVariant(index)}
                      className="tw-px-1 tw-text-red-600 tw-cursor-pointer"
                    />
                  </div>
                )}
                <div className="tw-flex tw-m-4 tw-justify-center">
                  <button
                    type="button"
                    onClick={() => handleAddvariant(index)}
                    className="btn btn-lg btn-success"
                  >
                    <FontAwesomeIcon
                      icon={faPlus}
                      size="md"
                      fixedWidth={true}
                      className="tw-px-1 tw-text-white"
                    />
                    Add Variant Set
                  </button>
                </div> */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

VariantSet.propTypes = {
  onVariantChange: PropTypes.func.isRequired,
  statusCode: PropTypes.number,
  handleBlur: PropTypes.func.isRequired,
  defaults: PropTypes.shape({
    variant: PropTypes.arrayOf(VariantSetType),
  }),
  setErrors: PropTypes.func.isRequired,
  setTouched: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  touched: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.shape({})),
    ])
  ).isRequired,
  hgvs: PropTypes.string,
  chrCoords: ChromosomeCoordinatesType,
};

VariantSet.defaultProps = {
  chrCoords: null,
  defaults: null,
  hgvs: "",
  statusCode: 0,
};
export default VariantSet;
